/**
 * Pages/Panel/ReportesNomina/ReportePercepcionesPorPeriodo.js
 *
 * Matriz de percepciones: un renglón por empleado, una columna por periodo y en
 * cada celda el total de percepciones que recibió ese empleado en ese periodo.
 *
 * La cifra es `LineaPrenomina.totalPercepciones`, el campo que ya materializa el
 * cálculo de la prenómina: sólo conceptos de tipo 1 y SIN los marcados "No Suma"
 * (informativos). No se re-suman los conceptos aquí — la regla ya está congelada
 * en ese campo, que es el mismo que ve el Sobre Recibo.
 *
 * Comparte endpoint con el Comparativo entre Periodos: ese $group ya devuelve
 * `percepciones` junto al neto, así que no hace falta backend propio. Lo que
 * cambia es la lectura: aquí no interesa la variación contra el periodo anterior,
 * sino cuánto sumó cada quien en el rango.
 *
 * Una celda sin recibo se escribe CERO (así se pidió), pero la ausencia sí se
 * conserva por dentro: es el denominador del promedio. Quien entró a media
 * temporada no debe verse castigado por los periodos en que no estuvo, y por eso
 * la columna "Periodos" va a la vista — sin ella la hoja parece mal sumada.
 */

import { message, PageHeader, Select, Space, Spin, Table, Tabs } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Form,
  FormItem,
  RangePickerItem,
  SelectItem,
  ButtonItem,
  ErrorItem,
  dineroDisabledMask,
} from "../../../Components/Items";
import SelectDepartamentosItem from "../../../Components/SelectDepartamentosItem";
import SelectEmpleadosItem from "../../../Components/SelectEmpleadosItem";
import { FileExcelOutlined } from "@ant-design/icons";
import * as XLSXStyle from "xlsx-js-style";
import moment from "moment";
import "moment/locale/es";
// Los layouts REPSE / Visor / Lista de Raya usan el encabezado gris de
// `estilosExcelNomina` a propósito, porque reproducen archivos del cliente. Éste
// es un reporte propio, así que va con la paleta de Biwo.
import { headerStyle, FORMATO_CONTABILIDAD, border } from "../../../Components/ExcelEstilos";
import {
  textoStyle,
  centroStyle,
  monedaStyle,
  celda,
  celdaNumero,
  celdaMoneda,
  filaDe,
} from "./estilosExcelNomina";

moment.locale("es");

const { Option } = Select;
const { TabPane } = Tabs;

// ── Estilos propios de esta hoja (paleta Biwo) ───────────────────────────────
const TEAL_OSCURO = "007A8A";
const AZUL_SUAVE = "D6F4F8";
const AZUL_TENUE = "F0FEFF";

const tituloStyle = {
  font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: TEAL_OSCURO } },
  alignment: { horizontal: "left", vertical: "center" },
};

const subtituloStyle = { font: { sz: 11, bold: true } };
const notaStyle = { font: { sz: 9, italic: true, color: { rgb: "595959" } } };

// Las columnas de Total / Periodos / Promedio llevan una franja tenue para
// despegarse visualmente de las columnas de periodo.
const resumenTextoStyle = {
  font: { bold: true },
  fill: { fgColor: { rgb: AZUL_TENUE } },
  alignment: { horizontal: "center", vertical: "center" },
  border,
};

const resumenMonedaStyle = {
  font: { bold: true },
  fill: { fgColor: { rgb: AZUL_TENUE } },
  alignment: { horizontal: "right", vertical: "center" },
  numFmt: FORMATO_CONTABILIDAD,
  border,
};

const totalTextoStyle = {
  font: { bold: true },
  fill: { fgColor: { rgb: AZUL_SUAVE } },
  alignment: { horizontal: "center", vertical: "center" },
  border,
};

const totalMonedaStyle = {
  ...totalTextoStyle,
  alignment: { horizontal: "right", vertical: "center" },
  numFmt: FORMATO_CONTABILIDAD,
};

const ETIQUETA_FINIQUITO = {
  todos: "Incluye finiquitos",
  sin: "Sólo nómina ordinaria (sin finiquitos)",
  solo: "Sólo finiquitos",
};

class ReportePercepcionesPorPeriodo extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    consultado: false,
    empresa: null,
    // Prenóminas del rango, para elegir cuáles entran a la matriz
    prenominas: [],
    seleccionadas: [],
    // Resultado
    columnas: [],
    filas: [],
    objetoRegistrosPatronales: null,
    // Un finiquito es una percepción real, así que el default es incluirlos.
    // 'todos' | 'sin' | 'solo'.
    filtroFiniquito: "todos",
  };

  atras() {
    this.props.history.goBack();
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  componentDidMount() {
    axios
      .post(
        "registrosPatronales/selectRegistrosPatronalesItem",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            objetoRegistrosPatronales: (res.data.registrosPatronales || []).map((value) => (
              <Option value={value._id} key={value._id}>
                {value.registroPatronalIMSS}
              </Option>
            )),
          });
        }
      })
      .catch(() => {});
  }

  consultarPrenominas(values) {
    this.setState({ loading: true });
    const [fechaInicio, fechaFin] = values.periodo || [];

    axios
      .post(
        "reportesNomina/prenominasParaReporte",
        {
          empresaId: this.props.empresaId,
          fechaInicio: fechaInicio ? fechaInicio.format("YYYY-MM-DD") : null,
          fechaFin: fechaFin ? fechaFin.format("YYYY-MM-DD") : null,
          tipoPeriodoId: null,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          const prenominas = res.data.prenominas || [];
          this.setState({
            loading: false,
            consultado: true,
            prenominas,
            // Se preseleccionan todas: el caso normal es querer el rango completo
            seleccionadas: prenominas.filter((p) => p.empleados > 0).map((p) => p._id),
            columnas: [],
            filas: [],
          });
          if (prenominas.length === 0) {
            message.info("No hay prenóminas con fecha de pago en ese rango");
          }
        } else if (res.data.success === 1) {
          message.warning(res.data.message, 8);
          this.setState({ loading: false });
        } else {
          message.error("Ha sucedido un error y no se cargaron las prenóminas");
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error("Ha sucedido un error y no se cargaron las prenóminas");
        this.setState({ loading: false });
      });
  }

  generarMatriz() {
    const { seleccionadas } = this.state;
    if (seleccionadas.length === 0) {
      return message.warning("Selecciona al menos un periodo");
    }

    const valores = this.formRef.current ? this.formRef.current.getFieldsValue() : {};
    this.setState({ loading: true });

    axios
      .post(
        "reportesNomina/reporteComparativoPeriodos",
        {
          empresaId: this.props.empresaId,
          prenominaIds: seleccionadas,
          registrosPatronales: valores.registrosPatronales || [],
          departamentos: valores.departamentos || [],
          empleados: valores.empleados || [],
          filtroFiniquito: this.state.filtroFiniquito,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            loading: false,
            empresa: res.data.empresa,
            columnas: res.data.columnas || [],
            filas: res.data.filas || [],
          });
        } else if (res.data.success === 1) {
          message.warning(res.data.message, 8);
          this.setState({ loading: false });
        } else {
          message.error("Ha sucedido un error y no se generó el reporte");
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error("Ha sucedido un error y no se generó el reporte");
        this.setState({ loading: false });
      });
  }

  // ── Cálculo compartido por la tabla en pantalla y el Excel ────────────────
  /**
   * Percepciones de un empleado en el orden de las columnas.
   *
   * Un periodo sin recibo se reporta como 0 (así se pidió el Excel), pero NO
   * cuenta para `presentes`: el promedio se divide entre los periodos en que el
   * empleado de verdad cobró, no entre el total de columnas.
   */
  percepcionesDeFila(fila) {
    const { columnas } = this.state;
    let presentes = 0;
    let total = 0;

    const valores = columnas.map((col) => {
      const dato = fila.periodos ? fila.periodos[col._id] : null;
      if (!dato) return 0;
      presentes += 1;
      const percepciones = dato.percepciones || 0;
      total += percepciones;
      return percepciones;
    });

    return {
      valores,
      presentes,
      total,
      promedio: presentes > 0 ? total / presentes : 0,
    };
  }

  rangoDeFechas() {
    const { columnas } = this.state;
    if (columnas.length === 0) return "";
    const inicios = columnas.map((c) => c.fechaInicio).filter(Boolean);
    const fines = columnas.map((c) => c.fechaFin).filter(Boolean);
    if (inicios.length === 0 || fines.length === 0) return "";
    const desde = moment.utc(inicios[0]);
    const hasta = moment.utc(fines[fines.length - 1]);
    return `Del ${desde.format("DD/MM/YYYY")} al ${hasta.format("DD/MM/YYYY")}`;
  }

  descargarExcel() {
    const { columnas, filas, empresa, filtroFiniquito } = this.state;
    if (filas.length === 0) return;

    const aoa = [];
    const anchoTotal = 4 + columnas.length + 3;

    // La banda va pintada celda por celda, no sólo en la esquina del merge: si
    // sólo se estiliza A1, Excel deja el resto de la franja en blanco.
    aoa.push(filaDe(anchoTotal, tituloStyle, { 0: empresa?.nombre || "" }));
    aoa.push([celda("Percepciones por periodo", subtituloStyle)]);
    aoa.push([
      celda(
        [this.rangoDeFechas(), `${columnas.length} periodo(s)`, ETIQUETA_FINIQUITO[filtroFiniquito]]
          .filter(Boolean)
          .join("  ·  "),
        notaStyle
      ),
    ]);
    aoa.push([]);

    // El nombre de la prenómina solo ("Semana 30", "Semana 31"…) no ubica nada:
    // las fechas del periodo van en el segundo renglón de la misma celda,
    // aprovechando el wrapText que ya trae headerStyle.
    const encabezado = [
      celda("Código", headerStyle),
      celda("Empleado", headerStyle),
      celda("Departamento", headerStyle),
      celda("Reg. Pat.", headerStyle),
    ];
    columnas.forEach((col) => {
      const desde = col.fechaInicio ? moment.utc(col.fechaInicio).format("DD/MM/YY") : "";
      const hasta = col.fechaFin ? moment.utc(col.fechaFin).format("DD/MM/YY") : "";
      const fechas = desde && hasta ? `\n${desde} - ${hasta}` : "";
      encabezado.push(celda(`${col.nombre}${fechas}`, headerStyle));
    });
    encabezado.push(celda("Total", headerStyle));
    encabezado.push(celda("Periodos", headerStyle));
    encabezado.push(celda("Promedio", headerStyle));
    aoa.push(encabezado);

    const totales = columnas.map(() => 0);
    let totalGeneral = 0;

    filas.forEach((fila) => {
      const datos = this.percepcionesDeFila(fila);
      const renglon = [
        celda(fila.codigo == null ? "" : String(fila.codigo).padStart(4, "0"), textoStyle),
        celda(fila.nombre, textoStyle),
        celda(fila.departamento, textoStyle),
        celda(fila.registroPatronalIMSS, centroStyle),
      ];
      datos.valores.forEach((valor, i) => {
        renglon.push(celdaMoneda(valor, monedaStyle));
        totales[i] += valor;
      });
      renglon.push(celdaMoneda(datos.total, resumenMonedaStyle));
      renglon.push(celdaNumero(datos.presentes, resumenTextoStyle));
      renglon.push(celdaMoneda(datos.promedio, resumenMonedaStyle));
      totalGeneral += datos.total;
      aoa.push(renglon);
    });

    const filaTotal = [
      celda("TOTAL", totalTextoStyle),
      celda("", totalTextoStyle),
      celda("", totalTextoStyle),
      celda("", totalTextoStyle),
    ];
    totales.forEach((total) => filaTotal.push(celdaMoneda(total, totalMonedaStyle)));
    filaTotal.push(celdaMoneda(totalGeneral, totalMonedaStyle));
    filaTotal.push(celda("", totalTextoStyle));
    filaTotal.push(celda("", totalTextoStyle));
    aoa.push(filaTotal);

    const hoja = XLSXStyle.utils.aoa_to_sheet(aoa);
    hoja["!cols"] = [
      { wch: 10 }, { wch: 34 }, { wch: 22 }, { wch: 16 },
      ...columnas.map(() => ({ wch: 15 })),
      { wch: 16 }, { wch: 10 }, { wch: 15 },
    ];
    // La banda del título abarca todo el ancho de la tabla.
    hoja["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(anchoTotal - 1, 1) } }];
    hoja["!rows"] = [{ hpt: 22 }];

    const libro = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(libro, hoja, "Percepciones");
    XLSXStyle.writeFile(
      libro,
      `Percepciones_Por_Periodo_${moment().format("YYYY_MM_DD")}.xlsx`
    );
  }

  render() {
    const {
      loading, consultado, prenominas, seleccionadas, columnas, filas, filtroFiniquito,
    } = this.state;

    // Las percepciones de cada empleado se calculan UNA vez por render, no dentro
    // del render de cada celda: con 12 periodos cada renglón recorrería las
    // columnas doce veces para pintar doce celdas.
    const datosPorEmpleado = new Map(
      filas.map((fila) => [fila.empleadoId, this.percepcionesDeFila(fila)])
    );

    const granTotal = filas.reduce(
      (suma, fila) => suma + (datosPorEmpleado.get(fila.empleadoId)?.total || 0),
      0
    );

    const columnasPrenominas = [
      { title: "Periodo", dataIndex: "periodoNumero", width: 90, render: (v) => (v == null ? "—" : v) },
      { title: "Nombre", dataIndex: "nombre" },
      { title: "Tipo", dataIndex: "tipoPeriodoNombre", width: 120 },
      {
        title: "Fecha de pago",
        dataIndex: "fechaPago",
        width: 120,
        align: "center",
        render: (v) => (v ? moment.utc(v).format("DD/MM/YYYY") : "—"),
      },
      { title: "Empleados", dataIndex: "empleados", width: 100, align: "right" },
    ];

    // Una columna por periodo, más las tres de resumen al final
    const columnasMatriz = [
      {
        title: "Código",
        dataIndex: "codigo",
        width: 90,
        fixed: "left",
        render: (v) => (v == null ? "—" : String(v).padStart(4, "0")),
      },
      { title: "Empleado", dataIndex: "nombre", width: 240, fixed: "left" },
      { title: "Departamento", dataIndex: "departamento", width: 160 },
      ...columnas.map((col, i) => ({
        title: (
          <span>
            {col.nombre}
            {col.fechaInicio && col.fechaFin && (
              <>
                <br />
                <small style={{ color: "#999", fontWeight: "normal" }}>
                  {moment.utc(col.fechaInicio).format("DD/MM/YY")} -{" "}
                  {moment.utc(col.fechaFin).format("DD/MM/YY")}
                </small>
              </>
            )}
          </span>
        ),
        key: col._id,
        width: 150,
        align: "right",
        render: (_, fila) => {
          const datos = datosPorEmpleado.get(fila.empleadoId);
          const valor = datos ? datos.valores[i] : 0;
          // El cero de "no estuvo" se atenúa: el número es el pedido, pero el
          // renglón no debe leerse como si hubiera cobrado en ese periodo.
          return valor === 0 ? (
            <span style={{ color: "#bbb" }}>{dineroDisabledMask(0)}</span>
          ) : (
            dineroDisabledMask(valor)
          );
        },
      })),
      {
        title: "Total",
        key: "total",
        width: 150,
        align: "right",
        render: (_, fila) => (
          <b>{dineroDisabledMask(datosPorEmpleado.get(fila.empleadoId)?.total || 0)}</b>
        ),
      },
      {
        title: "Periodos",
        key: "presentes",
        width: 90,
        align: "center",
        render: (_, fila) => datosPorEmpleado.get(fila.empleadoId)?.presentes || 0,
      },
      {
        title: "Promedio",
        key: "promedio",
        width: 150,
        align: "right",
        render: (_, fila) =>
          dineroDisabledMask(datosPorEmpleado.get(fila.empleadoId)?.promedio || 0),
      },
    ];

    return (
      <>
        <Form
          ref={this.formRef}
          name="reportePercepcionesPorPeriodo"
          onFinish={this.consultarPrenominas.bind(this)}
          onFinishFailed={this.onFinishFailed}
          initialValues={{
            periodo: [moment().startOf("month"), moment().endOf("month")],
          }}
        >
          <PageHeader
            onBack={this.atras.bind(this)}
            title="Percepciones por Periodo"
            style={{
              border: "1px solid rgb(235, 237, 240)",
              backgroundColor: "white",
              marginBottom: 10,
            }}
            extra={
              <div style={{ display: "flex", justifyContent: "right" }}>
                <Space>
                  {filas.length > 0 && (
                    <ButtonItem
                      blanco
                      onClick={() => this.descargarExcel()}
                      icon={<FileExcelOutlined />}
                      style={{ display: "block", margin: "0 0 0 auto" }}
                    >
                      Descargar Excel
                    </ButtonItem>
                  )}
                  <ButtonItem
                    type="primary"
                    htmlType="submit"
                    style={{ display: "block", margin: "0 0 0 auto" }}
                  >
                    Consultar
                  </ButtonItem>
                </Space>
              </div>
            }
          />
          <Spin spinning={loading}>
            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                paddingTop: 5,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                marginBottom: 10,
              }}
            >
              {/* forceRender en todas: sin él se pierden los campos de las
                  pestañas que el usuario no llegó a abrir. */}
              <Tabs
                style={{ margin: 0 }}
                tabBarExtraContent={{
                  left: <h1 style={{ padding: 0, margin: "0 10px 0 0" }}>Filtros:</h1>,
                }}
              >
                <TabPane tabKey="1" key="1" forceRender tab="Fecha de pago">
                  <FormItem name="periodo" required noStyle>
                    <RangePickerItem />
                  </FormItem>
                </TabPane>
                <TabPane tabKey="2" key="2" forceRender tab="Registro Patronal">
                  <FormItem name="registrosPatronales" noStyle margin>
                    <SelectItem
                      width="90%"
                      mode="multiple"
                      placeholder="Todos los registros patronales"
                    >
                      {this.state.objetoRegistrosPatronales}
                    </SelectItem>
                  </FormItem>
                </TabPane>
                <TabPane tabKey="3" key="3" forceRender tab="Departamentos">
                  <FormItem name="departamentos" noStyle margin>
                    <SelectDepartamentosItem
                      mode="multiple"
                      empresaId={this.props.empresaId}
                      headersToken={this.props.headersToken}
                      placeholder="Todos los departamentos"
                      style={{ width: "90%" }}
                    />
                  </FormItem>
                </TabPane>
                <TabPane tabKey="4" key="4" forceRender tab="Empleados">
                  <FormItem name="empleados" noStyle margin>
                    <SelectEmpleadosItem
                      mode="multiple"
                      incluirBajas
                      empresaId={this.props.empresaId}
                      headersToken={this.props.headersToken}
                      placeholder="Todos los empleados"
                      style={{ width: "90%" }}
                    />
                  </FormItem>
                </TabPane>
              </Tabs>
            </div>

            {consultado && (
              <div
                style={{
                  backgroundColor: "white",
                  padding: 20,
                  paddingTop: 10,
                  marginBottom: 15,
                  borderRadius: 5,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                }}
              >
                <Space wrap style={{ marginBottom: 12 }}>
                  <span style={{ color: "#666" }}>
                    Periodos del reporte ({seleccionadas.length} de {prenominas.length}):
                  </span>
                  <ButtonItem
                    type="primary"
                    disabled={seleccionadas.length === 0}
                    onClick={() => this.generarMatriz()}
                  >
                    Generar
                  </ButtonItem>
                  {/* Un finiquito se paga dentro de la prenómina ordinaria y es una
                      percepción real, así que el default los incluye. Se pueden
                      sacar para ver sólo el sueldo corriente, o mirar sólo ésos. */}
                  <span style={{ color: "#666", marginLeft: 12 }}>Finiquitos</span>
                  <Select
                    value={filtroFiniquito}
                    onChange={(v) => this.setState({ filtroFiniquito: v })}
                    style={{ width: 170 }}
                  >
                    <Option value="todos">
                      <span style={{ fontWeight: 600, color: "#00BFD6" }}>Incluir finiquitos</span>
                    </Option>
                    <Option value="sin">Sólo la ordinaria</Option>
                    <Option value="solo">Sólo finiquitos</Option>
                  </Select>
                </Space>

                <Table
                  size="small"
                  rowKey="_id"
                  dataSource={prenominas}
                  columns={columnasPrenominas}
                  pagination={{ defaultPageSize: 8 }}
                  rowSelection={{
                    selectedRowKeys: seleccionadas,
                    onChange: (keys) => this.setState({ seleccionadas: keys }),
                    getCheckboxProps: (record) => ({ disabled: record.empleados === 0 }),
                  }}
                />
              </div>
            )}

            {filas.length > 0 && (
              <div
                style={{
                  backgroundColor: "white",
                  padding: 20,
                  paddingTop: 10,
                  marginBottom: 15,
                  borderRadius: 5,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                }}
              >
                <p style={{ marginBottom: 10, color: "#666" }}>
                  {filas.length} empleado(s) · {columnas.length} periodo(s) · Total de
                  percepciones <b style={{ color: "#00BFD6" }}>{dineroDisabledMask(granTotal)}</b>
                </p>
                <Table
                  size="small"
                  rowKey="empleadoId"
                  dataSource={filas}
                  columns={columnasMatriz}
                  pagination={{ defaultPageSize: 20 }}
                  scroll={{ x: 1090 + columnas.length * 150 }}
                />
              </div>
            )}
          </Spin>
        </Form>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
  };
};

export default connect(mapStateToProps)(ReportePercepcionesPorPeriodo);
